import logdown from 'logdown';
import { Maybe, Some, None } from 'monet';
import pluralize from 'pluralize';
import * as React from 'react';
// @ts-ignore
import * as Icon from 'react-bootstrap-icons';
import { Typeahead } from 'react-bootstrap-typeahead';
import { connect } from 'react-redux';
import { returntypeof } from 'react-redux-typescript';
import { Button, Spinner, UncontrolledTooltip } from 'reactstrap';
import { bindActionCreators, Dispatch } from 'redux';

import { UserRole } from '../../auth/entities';
import { State } from '../../reducers';
import { UserState, CompaniesListingState } from '../user';
import { withClickConfirmation, DateTimeRender } from '../utils';
import { DateRangeFilter, DateRange } from '../utils/dateRangeFilter';
import { ServerfarmTable } from '../utils/serverfarmTable';
import { TextFilter } from '../utils/textFilter';

import {
  cloneTemplate,
  deleteTemplate,
  editTemplate,
  fetchTemplates,
  updateTemplateListingFilter,
  updateTemplateListingSort,
  updateTemplateListingPages,
} from './data/actions';
import { Template, TemplatesListSort } from './entities';
import { TemplatesListingState, TemplateDeletingState } from './redux/entities';

const logger = logdown('component:TemplatesList');

const ButtonWithConfirmation = withClickConfirmation(Button);

class NameFilter extends React.Component<Props & TemplatesListProps> {
  shouldComponentUpdate(nextProps: Props & TemplatesListProps) {
    const isIsInProgressChanged = nextProps.templatesListing.isInProgress !== this.props.templatesListing.isInProgress;
    const isTotalChanged = nextProps.templatesListing.total !== this.props.templatesListing.total;
    const shouldChange = isIsInProgressChanged || isTotalChanged;
    return shouldChange;
  }

  render() {
    return (
      <TextFilter
        placeholder={
          this.props.templatesListing.isInProgress
            ? 'Loading...'
            : `Search among ${this.props.templatesListing.total} ${pluralize('record', this.props.templatesListing.total)}...`
        }
        isSearching={this.props.templatesListing.isInProgress}
        onChange={(val) => {
          /**
           * Only set value if its changed. Consider '' === undefined
           */
          const valueToSet = val === '' || val === undefined ? undefined : val;
          if (this.props.templatesListing.filter.exists((filter) => filter.name !== valueToSet)) {
            this.props.updateTemplateListingFilter(
              this.props.templatesListing.filter.map((filter) => ({ ...filter, name: valueToSet })),
              this.props.templatesListing.page,
              this.props.templatesListing.sizePerPage,
            );
          }
        }}
      />
    );
  }
}

class ActionsFormatter extends React.Component<Props & TemplatesListProps & { template: any }> {
  shouldComponentUpdate(nextProps: Props & TemplatesListProps & { template: any }) {
    const isTemplateUpdated = nextProps.template.id !== this.props.template.id;
    const isDeletingInProgressChanged = nextProps.templateDeleting.isInProgress !== this.props.templateDeleting.isInProgress;

    const shouldChange = isTemplateUpdated || isDeletingInProgressChanged;
    return shouldChange;
  }

  render() {
    return (
      <span>
        <Button
          data-testid="edit-button"
          className="edit-button"
          id={`edit-button-${this.props.template.id}`}
          color="success"
          size="sm"
          onClick={() => this.props.editTemplate(Maybe.Some(this.props.template))}
        >
          <Icon.PencilSquare className="sm" />
        </Button>{' '}
        <Button
          data-testid="clone-button"
          className="clone-button"
          id={`clone-button-${this.props.template.id}`}
          color="primary"
          size="sm"
          onClick={() => this.props.cloneTemplate(this.props.template)}
        >
          <Icon.Files className="sm" />
        </Button>{' '}
        <ButtonWithConfirmation
          data-testid="delete-button"
          className="delete-button"
          id={`delete-button-${this.props.template.id}`}
          color="danger"
          size="sm"
          disabled={this.props.templateDeleting.isInProgress && this.props.templateDeleting.templateId.contains(this.props.template.id)}
          onClick={() => this.props.deleteTemplate(this.props.template.id)}
          popoverCaption="Delete Template?"
          popoverOkButton="Yes"
          popoverCancelButton="No"
        >
          {this.props.templateDeleting.isInProgress && this.props.templateDeleting.templateId.contains(this.props.template.id) ? (
            <Spinner size="sm" />
          ) : (
            <Icon.TrashFill className="sm" />
          )}
        </ButtonWithConfirmation>
        <UncontrolledTooltip target={`edit-button-${this.props.template.id}`}>Edit</UncontrolledTooltip>{' '}
        <UncontrolledTooltip target={`clone-button-${this.props.template.id}`}>Clone</UncontrolledTooltip>{' '}
        <UncontrolledTooltip target={`delete-button-${this.props.template.id}`}>Delete</UncontrolledTooltip>{' '}
      </span>
    );
  }
}

const SelectColumnFilter = (props: Props & TemplatesListProps) => (params: any) => {
  const selectedCompanyIds = props.templatesListing.filter
    .map((dsf) => {
      return dsf.companies || [];
    })
    .orJust([]);
  return (
    <Typeahead
      multiple={true}
      data-testid="data-source-filter-company"
      id="data-source-filter-company"
      isLoading={props.companiesListing.isInProgress}
      onChange={(selected) => {
        const filter = props.templatesListing.filter.map((filter) => ({
          ...filter,
          companies: selected.map((c) => c.id.toString()),
        }));
        props.updateTemplateListingFilter(filter, props.templatesListing.page, props.templatesListing.sizePerPage);
      }}
      options={props.companiesListing.companies.getOrElse([])}
      emptyLabel="No Companies found"
      placeholder="Select a company"
      defaultSelected={props.companiesListing.companies.map((carr) => carr.filter((c) => selectedCompanyIds.includes(c.id.toString()))).getOrElse([])}
      labelKey="name"
      disabled={props.templatesListing.isInProgress}
    />
  );
};

const DateRangeFilterCreator = (props: Props & { templatesListing: TemplatesListingState }) => (
  <DateRangeFilter
    id="date-range-filter-created"
    value={props.templatesListing.filter.flatMap((filter) => filter.created)}
    onReset={() => {
      props.updateTemplateListingFilter(
        props.templatesListing.filter.flatMap((filter) =>
          Some({
            ...filter,
            created: None(),
          }),
        ),
        props.templatesListing.page,
        props.templatesListing.sizePerPage,
      );
    }}
    onSave={(range: DateRange) => {
      props.updateTemplateListingFilter(
        props.templatesListing.filter.map((filter) => ({
          ...filter,
          created: Some(range),
        })),
        props.templatesListing.page,
        props.templatesListing.sizePerPage,
      );
    }}
  />
);

const UpdatedDateRangeFilter = (props: Props & { templatesListing: TemplatesListingState }) => (
  <DateRangeFilter
    id="date-range-filter-updated"
    value={props.templatesListing.filter.flatMap((filter) => filter.updated)}
    onReset={() => {
      props.updateTemplateListingFilter(
        props.templatesListing.filter.flatMap((filter) =>
          Some({
            ...filter,
            updated: None(),
          }),
        ),
        props.templatesListing.page,
        props.templatesListing.sizePerPage,
      );
    }}
    onSave={(range: DateRange) => {
      props.updateTemplateListingFilter(
        props.templatesListing.filter.map((filter) => ({
          ...filter,
          updated: Some(range),
        })),
        props.templatesListing.page,
        props.templatesListing.sizePerPage,
      );
    }}
  />
);
const columns = (props: Props & TemplatesListProps) => [
  {
    id: 'name',
    width: 'auto',
    Header: 'Name',
    accessor: (row: Template) => {
      const className = 'templates-list-item-name';
      return (
        <span id={`${className}-${row.id}`} className={className}>
          {row.name}
        </span>
      );
    },
    Filter: <NameFilter {...props} />,
  },
  {
    width: 200,
    Header: 'Company',
    accessor: (row: Template) => {
      const className = 'templates-list-item-company';
      return (
        <span id={`${className}-${row.id}`} className={className}>
          {row.company.map((c) => c.name).getOrElse('N/A')}
        </span>
      );
    },
    // Cell: (cell: any) => renderCompanyColumn(props, cell),
    Filter: SelectColumnFilter(props),
    disableFilters: props.user.profile.role.equals(Some(UserRole.COMMON_USER)),
    disableSortBy: true,
  },
  {
    id: 'created',
    width: 200,
    Header: 'Created',
    accessor: (row: Template) => {
      const className = 'templates-list-item-created';
      return <DateTimeRender id={`${className}-${row.id}`} className={className} date={row.created} timeZoneRef={props.user.profile.timeZoneRef} />;
    },
    Filter: DateRangeFilterCreator(props),
  },
  {
    id: 'updated',
    width: 200,
    Header: 'Updated',
    accessor: (row: Template) => {
      const className = 'templates-list-item-updated';
      return <DateTimeRender id={`${className}-${row.id}`} className={className} date={row.updated} timeZoneRef={props.user.profile.timeZoneRef} />;
    },
    Filter: UpdatedDateRangeFilter(props),
  },
  {
    width: 155,
    Header: 'Actions',
    accessor: (row: Template) => <ActionsFormatter {...props} template={row} />,
    disableFilters: true,
    disableSortBy: true,
  },
];

interface TemplatesListProps {
  user: UserState;
  templatesListing: TemplatesListingState;
  templateDeleting: TemplateDeletingState;
  companiesListing: CompaniesListingState;
}

class Component extends React.Component<Props & TemplatesListProps, {}> {
  componentDidMount() {
    this.props.updateTemplateListingFilter(
      Some({ created: None(), updated: None() }),
      this.props.templatesListing.page,
      this.props.templatesListing.sizePerPage,
    );
  }

  componentDidUpdate(prevProps: Props & TemplatesListProps) {
    const isFilterUpdated = !prevProps.templatesListing.filter.equals(this.props.templatesListing.filter);
    const isSortingUpdated = !prevProps.templatesListing.sort.equals(this.props.templatesListing.sort);
    const isPageSizeUpdated = !(prevProps.templatesListing.sizePerPage === this.props.templatesListing.sizePerPage);
    const isPageChanged = !(prevProps.templatesListing.page === this.props.templatesListing.page);
    if (isSortingUpdated || isFilterUpdated || isPageSizeUpdated || isPageChanged) {
      this.props.fetchTemplates(
        this.props.templatesListing.filter,
        this.props.templatesListing.page,
        this.props.templatesListing.sizePerPage,
        this.props.templatesListing.sort,
      );
      return;
    }
  }

  public render() {
    const pageCount = Math.floor(this.props.templatesListing.total / this.props.templatesListing.sizePerPage) + 1;
    return (
      <div className="content">
        <ServerfarmTable
          columns={columns(this.props)}
          data={this.props.templatesListing.templates.orJust([])}
          onPageChange={(pageIndex, pageSize) => {
            if (this.props.templatesListing.page !== pageIndex || this.props.templatesListing.sizePerPage !== pageSize) {
              this.props.updateTemplateListingPages(pageIndex, pageSize);
            }
          }}
          onSortChange={(sortBy) => {
            const newSort: Maybe<TemplatesListSort> =
              sortBy.length > 0 ? Some({ field: sortBy[0].id, order: sortBy[0].desc ? 'desc' : 'asc' }) : None();
            this.props.templatesListing.sort.equals(newSort);
            if (!this.props.templatesListing.sort.equals(newSort)) {
              this.props.updateTemplateListingSort(newSort);
            }
          }}
          total={this.props.templatesListing.total}
          controlledPageCount={pageCount}
          initialPaginationState={{
            pageIndex: this.props.templatesListing.page,
            pageSize: this.props.templatesListing.sizePerPage,
            sortBy: this.props.templatesListing.sort.map((s) => [{ id: s.field, desc: s.order === 'desc' }]).orJust([]),
          }}
          isLoading={this.props.templatesListing.isInProgress}
        />
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch: Dispatch<any>) =>
  bindActionCreators(
    {
      fetchTemplates,
      cloneTemplate,
      deleteTemplate,
      editTemplate,
      updateTemplateListingFilter,
      updateTemplateListingSort,
      updateTemplateListingPages,
    },
    dispatch,
  );

const mapStateToProps = (state: State) => ({});

const stateProps = returntypeof(mapStateToProps);
const dispatchProps = returntypeof(mapDispatchToProps);

type Props = typeof stateProps & typeof dispatchProps;

export default connect<typeof stateProps, typeof dispatchProps, any>(
  // @ts-ignore
  mapStateToProps,
  mapDispatchToProps,
)(Component);
